'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface PinVerificationModalProps {
  isOpen: boolean;
  onVerify: () => void;
  onCancel: () => void;
  userId: string;
}

const PinVerificationModal = ({ isOpen, onVerify, onCancel, userId }: PinVerificationModalProps) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

  useEffect(() => {
    if (isOpen) {
      // Focus first input when modal opens
      inputRefs[0].current?.focus();
      // Reset state
      setPin(['', '', '', '']);
      setError('');
    }
  }, [isOpen]);

  const handlePinChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^[0-9]$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits are entered
    if (index === 3 && value) {
      const fullPin = newPin.join('');
      if (fullPin.length === 4) {
        handleVerify(fullPin);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (/^[0-9]{4}$/.test(pastedData)) {
      const newPin = pastedData.split('');
      setPin(newPin);
      inputRefs[3].current?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (pinValue?: string) => {
    const fullPin = pinValue || pin.join('');
    
    if (fullPin.length !== 4) {
      setError('Please enter all 4 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const supabase = createClient();
      
      // Call the verify_admin_pin function
      const { data, error: verifyError } = await supabase.rpc('verify_admin_pin', {
        user_id: userId,
        pin: fullPin
      });

      if (verifyError) {
        if (verifyError.message.includes('locked')) {
          setError('Account locked. Too many failed attempts.');
        } else if (verifyError.message.includes('not set')) {
          setError('PIN not configured. Please contact administrator.');
        } else {
          setError('Invalid PIN. Please try again.');
        }
        setPin(['', '', '', '']);
        inputRefs[0].current?.focus();
        return;
      }

      if (data === true) {
        onVerify();
      } else {
        setError('Invalid PIN. Please try again.');
        setPin(['', '', '', '']);
        inputRefs[0].current?.focus();
      }
    } catch (err: any) {
      setError(err?.message || 'Verification failed. Please try again.');
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl border border-border p-8 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldCheckIcon" size={32} className="text-primary" />
          </div>
          <h2 className="font-jakarta font-bold text-2xl text-foreground mb-2">
            Admin Verification
          </h2>
          <p className="text-muted-foreground text-sm">
            Enter your 4-digit PIN to continue
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded-xl text-error text-sm text-center">
            {error}
          </div>
        )}

        {/* PIN Input */}
        <div className="flex justify-center gap-3 mb-8">
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handlePinChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={loading}
              className="w-14 h-14 text-center text-2xl font-bold bg-input border-2 border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            />
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 bg-secondary text-secondary-foreground rounded-xl font-semibold hover:bg-secondary/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleVerify()}
            disabled={loading || pin.join('').length !== 4}
            className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          <Icon name="InformationCircleIcon" size={16} className="inline mr-1" />
          Account will be locked for 15 minutes after 5 failed attempts
        </div>
      </div>
    </div>
  );
};

export default PinVerificationModal;